"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import Image from "next/image";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { optimizeCloudinaryUrl } from "@/lib/cloudinary";
import { API_URL } from "@/lib/constants";

interface BannerData {
  _id: string;
  imageUrl: string;
  alt?: string;
  link?: string;
}

// Constants for aspect ratio (1169x300)
const IMG_W = 1169;
const IMG_H = 300;
const SLIDE_DURATION = 4000;

const ensureAbsoluteUrl = (url: string) => {
  if (!url) return "";
  let cleanUrl = url.replace(/[\u200B-\u200D\uFEFF]/g, "").trim();
  if (/^https?:\/\//i.test(cleanUrl)) return cleanUrl;
  if (cleanUrl.startsWith("/") || cleanUrl.startsWith("//")) return cleanUrl;
  if (/^[a-z]+[:\/]/i.test(cleanUrl)) {
    cleanUrl = cleanUrl
      .replace(/^[a-z]+[:\/\.]*/i, "")
      .replace(/^[:\/\.]+/, "");
  }
  return `https://${cleanUrl}`;
};

interface BannerProps {
  initialBanners?: any[];
}

export default function Banner({ initialBanners = [] }: BannerProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [slides, setSlides] = useState<any[]>(initialBanners);
  const [isLoading, setIsLoading] = useState(initialBanners.length === 0);

  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const nextSlide = useCallback(() => {
    if (slides.length === 0) return;
    setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  }, [slides.length]);

  const prevSlide = useCallback(() => {
    if (slides.length === 0) return;
    setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  }, [slides.length]);

  // Fetch Banners
  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const response = await fetch(`${API_URL}/banners`);
        if (!response.ok) {
          throw new Error(`Failed to fetch banners: ${response.statusText}`);
        }
        const data: BannerData[] = await response.json();
        if (data && data.length > 0) {
          const dynamicSlides = data.map((item) => ({
            id: item._id,
            src: item.imageUrl,
            alt: item.alt || "Banner image",
            link: item.link || "",
          }));
          setSlides(dynamicSlides);
        }
      } catch (error) {
        console.error("Error fetching banners:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (initialBanners.length === 0) {
      fetchBanners();
    } else {
      setIsLoading(false);
    }
  }, [initialBanners]);

  // Auto-play Logic
  useEffect(() => {
    if (isHovering || slides.length <= 1) return;
    const interval = setInterval(nextSlide, SLIDE_DURATION);
    return () => clearInterval(interval);
  }, [nextSlide, isHovering, slides.length]);

  // Touch Handlers for Mobile Swiping
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    touchEndX.current = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      diff > 0 ? nextSlide() : prevSlide();
    }
  };

  if (isLoading) {
    return (
      <section className="bg-white">
        <div
          className="relative w-full overflow-hidden rounded-sm bg-gray-100 animate-pulse flex items-center justify-center border-b-[4px] border-[#00a859]"
          style={{ aspectRatio: "1169 / 300" }}
        >
          <div className="w-24 h-24 bg-white rounded-full animate-ping" />
        </div>
      </section>
    );
  }

  return (
    <section className="bg-white pt-4">
      <div className="relative w-full overflow-hidden border-b-[4px] border-white">
        {slides.length > 0 ? (
          <div
            className="relative w-full"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            {/* Aspect Ratio Sizer */}
            <svg
              viewBox={`0 0 ${IMG_W} ${IMG_H}`}
              className="w-full block"
              aria-hidden="true"
            />

            {/* Slide Track */}
            <div className="absolute inset-0">
              {slides.map((slide, i) => {
                const isActive = i === currentSlide;
                return (
                  <div
                    key={slide.id}
                    className={`absolute inset-0 w-full h-full transition-opacity duration-700 ease-in-out ${
                      isActive ? "opacity-100 z-10" : "opacity-0 z-0"
                    }`}
                  >
                    {slide.link ? (
                      <a
                        href={ensureAbsoluteUrl(slide.link)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="relative block h-full w-full"
                      >
                        <Image
                          src={slide.src}
                          alt={slide.alt}
                          fill
                          sizes="100vw"
                          className="object-cover"
                          priority={i === 0}
                          fetchPriority={i === 0 ? "high" : "auto"}
                        />
                      </a>
                    ) : (
                      <Image
                        src={slide.src}
                        alt={slide.alt}
                        fill
                        sizes="100vw"
                        className="object-cover"
                        priority={i === 0}
                        fetchPriority={i === 0 ? "high" : "auto"}
                      />
                    )}
                  </div>
                );
              })}
            </div>

            {/* Navigation Arrows */}
            {slides.length > 1 && (
              <>
                <button
                  onClick={prevSlide}
                  aria-label="Previous slide"
                  className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-20 bg-black/20 hover:bg-black/40 text-white p-1.5 md:p-3 rounded-full transition-all flex items-center justify-center shadow-md"
                >
                  <ChevronLeft className="w-5 h-5 md:w-8 md:h-8" />
                </button>
                <button
                  onClick={nextSlide}
                  aria-label="Next slide"
                  className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-20 bg-black/20 hover:bg-black/40 text-white p-1.5 md:p-3 rounded-full transition-all flex items-center justify-center shadow-md"
                >
                  <ChevronRight className="w-5 h-5 md:w-8 md:h-8" />
                </button>
              </>
            )}
          </div>
        ) : (
          /* Fallback UI */
          <div 
            className="w-full bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center text-center"
            style={{ aspectRatio: "1169 / 300" }}
          >
            <p className="text-xl md:text-4xl font-bold text-gray-800">
              Karnataka Agribusiness
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
